import {
  EventEmitter, Injectable, OnDestroy, OnInit,
} from '@angular/core';
import { P } from '@ui-kit/ui-kit';
import { Subscription } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import { LeanFavourite } from '../../public/favourites/shortlist.helpers';
import { UserService } from './user.service';
import { environment } from '../../../environments/environment';

type IneligibleReason = 'residency status' | 'deposit' | 'income' | 'product interest';

export type DashboardData = {
  signedIn: boolean;
  username: string;
  qualified: boolean;
  qualifiesForBookACall: boolean;
  onboarded: boolean;
  year: number;
  step: number;
  logoURL: string;
  favouritesCount: number;
  profileComplete: number;
  broker: P.Broker;
  loan: P.LoanApplication;
  application: Partial<P.Application>;
  unsignedApplicationFile: P.LoanFile | null;
  unsignedIODFile: P.LoanFile | null;
  listing: P.Listing;
  brokerAssessment: P.BrokerAssessment;
  reasons: IneligibleReason[];
  showIncreaseSalary: boolean;
  showResidency: boolean;
  showSavings: boolean;
  showEarlyAccess: boolean;
  budget: number;
  hasMortgageOffer: boolean;
  userId: string;
  uploadTarget: Partial<P.Application> | P.LoanApplication;
  uploadStages: {
    identificationAndNationality: boolean;
    proofOfAddress: boolean;
    incomeAndExpenditure: boolean;
  };
  uploadTargetId: 'application' | 'loan';
  toggle: Dictionary<boolean>;
  officeAddress: string;
  offerPackFiles: P.ApplicationFile[];
};

export type DashboardStep = 'mip' | 'homes' | 'offer' | 'upload' | 'complete';

export const steps: { [K in DashboardStep]: number } = {
  mip: 0, homes: 1, offer: 2, upload: 3, complete: 4,
};

export type EventType =
  'download-unsigned-application-file' |
  'download-unsigned-iod-file' |
  'download-offer-pack' |
  'uploads-documents' |
  'update'

@Injectable({
  providedIn: 'root',
})
export class DashboardService {

  events: EventEmitter<{ type: EventType, data?: any }> = new EventEmitter();

  data: DashboardData = this.reset();

  private subscriptions: Subscription[] = [];

  constructor(private user: UserService) {
    this.subscriptions = [

      this.user.signedInObservable
        .pipe(
          startWith(this.user.signedIn),
          tap(() => this.onCurrentUserChange()))
        .subscribe(this.onSignedInChange.bind(this)),

      this.user.change
        // .pipe(startWith(this.user.current))
        .subscribe(this.onCurrentUserChange.bind(this)),

      this.user.mipObservable
        // .pipe(startWith(this.user.myHome))
        .subscribe(this.onMipChange.bind(this)),

      this.user.favouritesObservable
        // .pipe(startWith(this.user.favourites))
        .subscribe(this.onFavouritesChange.bind(this)),

      this.user.budgetObservable
        // .pipe(startWith(this.user.budget))
        .subscribe(this.onBudgetChange.bind(this)),

      this.user.myHomeObservable
        // .pipe(startWith(this.user.myHome))
        .subscribe(this.onMyHomeChange.bind(this)),

      this.user.loanObservable
        .subscribe(this.onLoanChange.bind(this)),
    ];

    this.user
      .update({}, 'dashboard-init')
      .toPromise();
  }

  dispose(): void {
    this.subscriptions
      .forEach((item) => item.unsubscribe());
    this.subscriptions = [];
  }

  private onCurrentUserChange(user: P.User<P.Applicant> | undefined = this.user.current) {

    const segment = user?.application?.qualifier?.segment || '';
    this.data.qualified = this.user.preQualifies;
    this.data.qualifiesForBookACall = this.user.qualifiesForBookACall;
    this.data.onboarded = !!(user?.application?.property_price);
    this.data.username = user?.first_name; // user?.display_name || `${user?.first_name} ${user?.last_name}`;
    this.data.profileComplete = this.user.profileComplete?.total || 0;
    this.data.broker = this.user.broker;
    this.data.loan = this.user.application?.loan;
    this.data.application = user?.application;
    this.data.listing = this.user.application?.my_home?.listing;
    this.data.brokerAssessment = this.user.current?.application?.broker_assessment;
    this.data.offerPackFiles = this.data.loan?.filesv2?.filter((file) => file.fileType === 'offerPackFile');
    this.data.hasMortgageOffer = this.data.offerPackFiles?.filter((offer) => !offer.invalid)?.length > 0 || false;
    this.data.userId = user?.id;
    this.data.uploadTarget = user?.application?.loan || user?.application;

    const supportingFiles = this.data.uploadTarget.supportingFiles;
    this.data.uploadStages = {
      identificationAndNationality: supportingFiles?.identificationAndNationality?.length >= 1,
      proofOfAddress: supportingFiles?.proofOfAddress?.length >= 1,
      incomeAndExpenditure: supportingFiles?.bankStatements?.length >= 3 && supportingFiles?.payslips?.length >= 3,
    };

    this.data.uploadTargetId = user?.application?.loan
      ? 'loan'
      : 'application';

    const loan = user?.application?.loan;
    this.data.unsignedApplicationFile = [
      ...((loan?.unsignedApplicationFile || [])
        .filter((file) => !file.invalid))
    ].pop();
    this.data.unsignedIODFile = [
      ...((loan?.unsignedInterestOnlyDeclarationFile || [])
        .filter((file) => !file.invalid))
    ].pop();

    this.events.emit({ type: 'update', data: this.data });
    this.user
      .qualifierBreakdown(user?.application)
      .then(breakdown => {
        const applicants = user.application?.applicants;

        this.data.reasons = [];

        if (breakdown.hasBelowMinDeposit) {
          this.data.reasons.push('deposit');
        }
        if (breakdown.hasBelowMinIncome) {
          this.data.reasons.push('income');
        }
        if (breakdown.hasIneligibleResidenceType) {
          this.data.reasons.push('residency status');
        }

        this.data.showIncreaseSalary = breakdown.hasBelowMinIncome
          && applicants.length < 2;

        this.data.showResidency = breakdown.hasIneligibleResidenceType;

        this.data.showSavings = breakdown.hasBelowMinDeposit;

        this.data.showEarlyAccess = breakdown.isNotInterrestedInMortgageBooster;

        this.events.emit({ type: 'update', data: this.data });
      });
  }

  private onSignedInChange(signedIn: boolean) {
    this.data.signedIn = signedIn;

    if (!signedIn) {
      this.reset();
    }

    this.onCurrentUserChange();
  }

  private onMipChange(mip: P.LoanFile[]) {
    if (this.data.broker && (mip || []).filter((file) => !file.invalid).length > 0) {
      this.data.step = steps.homes;
    }
  }

  private onFavouritesChange(favourites: LeanFavourite[]) {
    if (this.hasEligibleProperties(favourites)) {
      this.data.step = steps.offer;
    }
    this.data.favouritesCount = favourites.length;
  }

  private onMyHomeChange(myHome: LeanFavourite | undefined) {
    if (myHome) {
      this.data.step = steps.upload;
    }
  }

  private onLoanChange(loan: P.LoanApplication | undefined) {
    if (loan?.completed) {
      this.data.step = steps.complete;
    }
  }

  private onBudgetChange(budget: number) {
    this.data.budget = budget;
  }

  private reset(): DashboardData {
    this.data = {
      year: new Date().getFullYear(),
      logoURL: environment.urls.www,
      favouritesCount: 0,
      profileComplete: 0,
      step: steps.mip,
      budget: 0,
      signedIn: false,
      username: '',
      qualified: false,
      qualifiesForBookACall: false,
      onboarded: false,
      reasons: [],
      showIncreaseSalary: false,
      showResidency: false,
      showSavings: false,
      showEarlyAccess: false,
      broker: null,
      loan: null,
      application: null,
      listing: null,
      brokerAssessment: null,
      hasMortgageOffer: false,
      userId: '',
      uploadTarget: null,
      uploadStages: {
        identificationAndNationality: false,
        proofOfAddress: false,
        incomeAndExpenditure: false,
      },
      uploadTargetId: 'loan',
      unsignedApplicationFile: null,
      unsignedIODFile: null,
      toggle: {},
      officeAddress: 'GG 405, Metal Box Factory, 30 Great Guildford St, London SE1 0HS',
      offerPackFiles: [],
    };

    this.events.emit({ type: 'update', data: this.data });

    return this.data;
  }

  private hasEligibleProperties(favourites: LeanFavourite[]) {
    return favourites.filter(({ canonicalStatus }) => canonicalStatus === 'Eligible').length > 0;
  }
}
